import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent } from '@material-ui/core';
import { ScaleLoader } from 'react-spinners';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import * as R from 'ramda';
import _ from 'lodash';
import { TreeNode } from 'react-dropdown-tree-select';
import { useStyles } from '../../custom-forms';
import { ConfirmDialog } from '../../custom-forms';
import { useAppSelector, useAppDispatch } from '../../store/configureStore';
import { CaseSettingsActionCreators, CaseSettingsState, AppState } from '../../store';
import { ApplicationContext } from '../../context/Contexts';
import Referentials from '../../helpers/Referentials.json';
import {
  ReferentialCode,
  ParametreCode,
  CalculationSectionCodeEnum,
  OrganizationTypeCode,
  MakeException,
  CarPartQualityRatings,
  CaseStatusCodeEnum,
  OriginalPartShortNumberException,
  CountryCodeEnum
} from '../../helpers/Constants';
import { FormCode } from '../../helpers/forms/FormCode';
import { VehicleHelper } from '../../helpers/VehicleHelper';
import { captureStockExceededConfirm } from '../../helpers/Sentry';
import {
  CarClassRentPrice,
  CaseCalculationAlternativePart,
  CaseCalculationHistory,
  CaseCalculationPart,
  CaseInfo,
  GetGTSupplierMappingRequestDto,
  ManipulationTariffPercentResponse,
  AlternativeCalculationDialogState,
  Offer,
  OfferLine,
  OfferPart,
  OriginalPart,
  OrganizationBranch,
  PartNotifySuppportRequest,
  TariffPercentResponse,
  VehicleDto,
  ChangeCaseStatusRequest,
  GetPackagePartSelectionsRequestDto
} from '../../interfaces/Case';
import { CalculationRequest } from '../../interfaces/GtService';
import { WorkflowSectionStepForm } from '../../interfaces/Workflow';
import { IReferential } from '../../interfaces/IReferential';
import { Order, OrderPart } from '../../interfaces/Order';
import {
  buildOrderedCalculationParts,
  convertCalculationPartsToOrderParts,
  isPartAlreadyOrdered
} from '../../utils/orderUtils';
import { AlternativeCalculationContent } from './AlternativeCalculationContent';
import { CalculationActions, SaveValidationResult } from './types/alternative-calculation.types';
import {
  getParentParts,
  compareByExpectedDelivery,
  computeTotals,
  EMPTY_TOTALS,
  getPackageFromCalculationParts,
  deselectAllVariants,
  INITIAL_STATE,
  isBelowMinVariants,
  isInterogablePart,
  isNonStockDescriptionMissing,
  isZeroPriceOwnStockPart,
  packingErrorQuantity,
  selectedParent,
  revertAlternativeMapping,
  updatePart,
  uuidToNumber
} from './utils/calculation.utils';
import {
  emptyCalculationPartDraft,
  makeAlternativePart,
  makeCalculationPart
} from './data/factories';
import { CalculationFlagsProvider } from './context/CalculationFlagsContext';
import AlternativeCalculationHeader from './components/AlternativeCalculationHeader';
import { useCalculationPartsActions } from './hooks/useCalculationPartsActions';

export interface AlternativeCalculationDialogProps {
  onClose: () => void;
  calculationHistoryId: number;
  stepFormId: number;
  orderList: Order[];
  caseInfo: CaseInfo | null;
  closeDialog: () => void;
  manipulationTariff: ManipulationTariffPercentResponse;
  insurerList: IReferential[];
  serviceId: number | null;
  additionTariff: TariffPercentResponse;
  discountTariff: TariffPercentResponse;
  packageCalculationId: number | null;
  alternativePartVariantsEnabled: boolean;
  isOffline: boolean;
}

const AlternativeCalculationDialog: React.FC<AlternativeCalculationDialogProps> = ({
  onClose,
  calculationHistoryId,
  stepFormId,
  closeDialog,
  orderList,
  caseInfo,
  manipulationTariff,
  insurerList,
  serviceId,
  additionTariff,
  discountTariff,
  packageCalculationId,
  alternativePartVariantsEnabled,
  isOffline
}) => {
  const c = useStyles();
  const context = useContext(ApplicationContext);
  const referentialService = context.referentialService;
  const caseService = context.caseService;
  const organizationService = context.organizationService;
  const appUserService = context.appUserService;
  const translatorService = context.translatorService;
  const { enqueueSnackbar } = useSnackbar();
  const params = useParams<{ id: string }>();
  const caseSettingsState = useAppSelector((s) => s.caseSettings) as CaseSettingsState;
  const appState = useAppSelector((s) => s.app) as AppState;
  const dispatch = useAppDispatch();

  const [executing, setExecuting] = useState(false);
  const [isConfirm, setIsConfirm] = useState(false);
  const [isModifiedConfirmOpen, setIsModifiedConfirmOpen] = useState(false);

  const [isOrgPartnerConfirmOpen, setIsOrgPartnerConfirmOpen] = useState(false);
  const [gatedAction, setGatedAction] = useState<(() => void) | null>(null);

  const [state, setState] = useState<AlternativeCalculationDialogState>(INITIAL_STATE);

  const [calculationParts, setCalculationParts] = useState<CaseCalculationPart[]>([]);

  const variantsEnabled = alternativePartVariantsEnabled;

  const getPartsLocationOrganizationId = (): number | null => {
    return (
      state.selectedOrganizationBranch?.organizationId ??
      caseSettingsState.case?.serviceId ??
      appState.appUser!.organizationId
    );
  };

  const {
    toggleExpand,
    interogate,
    interogateAlternativeParts,
    handleAlternativePartSelect,
    toggleAlternativePartSelection,
    clearRecommendation,
    unselectPart,
    unselectAll,
    updateGuideNumber,
    updatePieceNr,
    updateOriginalPrice,
    updateCarPartCategory,
    updateAlternativePartDescription,
    applyOilMapping,
    getInactiveWorkflowSuppliers,
    getNonStockSuppliers,
    packingErrorMessage,
    validateSelectedParts
  } = useCalculationPartsActions({
    calculationParts,
    setCalculationParts,
    additionTariff,
    discountTariff,
    workflow: caseSettingsState.caseSettings!.workflow,
    workflowCalculation: state.workflowCalculation,
    ownStockOrganization: state.ownStockOrganization,
    variantsEnabled,
    nonStockSuppliers: state.nonStockSuppliers,
    setNonStockSuppliers: (suppliers) =>
      setState((prev) => ({ ...prev, nonStockSuppliers: suppliers })),
    getPartsLocationOrganizationId,
    getInterrogationOrganizationId: getPartsLocationOrganizationId,
    interrogationSupplier: null,
    reuseGroupedUniqueId: false
  });

  const rowRefs = useRef<Record<number, HTMLElement | null>>({});
  const calcPartsRef = useRef<CaseCalculationPart[]>([]);
  calcPartsRef.current = calculationParts;

  const registerRow = useCallback((id: number, el: HTMLElement | null) => {
    if (el) {
      rowRefs.current[id] = el;
    } else {
      delete rowRefs.current[id];
    }
  }, []);

  const totals = useMemo(
    () =>
      state.calculationSectionList.length
        ? computeTotals(
            state.calculationSectionList,
            calculationParts,
            parseInt(state.TVA) || 0,
            state.caseCalculationHistoryDetails.smallPartsPercent ?? 0,
            state.caseCalculationHistoryDetails.calculationType?.code ?? '',
            state.caseCalculationHistoryDetails.currencyCode ?? ''
          )
        : EMPTY_TOTALS,
    [calculationParts, state.calculationSectionList, state.TVA, state.caseCalculationHistoryDetails]
  );

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newCalculationPart, setNewCalculationPart] = useState<CaseCalculationPart>(
    () =>
      ({
        ...emptyCalculationPartDraft(),
        originalPrice: null,
        pieceNr: null
      } as unknown as CaseCalculationPart)
  );
  const [calculationPartFormTypeAdd, setCalculationPartFormTypeAdd] = useState(true);

  const [isOptimConfirmOpen, setIsOptimConfirmOpen] = useState(false);

  const loadCalculationDetails = async () => {
    const caseSettings = caseSettingsState.caseSettings;
    const caseId = Number.parseInt(params.id);
    if (caseSettings === null || caseSettings === undefined || Number.isNaN(caseId)) {
      return;
    }
    const casee = caseSettingsState.case;
    const TVA = caseSettingsState.case!.caseParameters.find(
      (item) => item.parameter!.code === ParametreCode.TVA
    )!.value;

    if (casee === null) {
      return;
    }

    const refQualityPart = Referentials.referential.find(
      (item) => item.code === ReferentialCode.CarPartCategory
    );
    const currencyRef = Referentials.referential.find(
      (item) => item.code === ReferentialCode.Currency
    );
    const refCaseStatus = Referentials.referential.find(
      (item) => item.code === ReferentialCode.CaseStatus
    );
    const refMaintenancePackage = Referentials.referential.find(
      (item) => item.code === ReferentialCode.MaintenancePackage
    );

    const orderPartList: OrderPart[] = [];
    orderList.forEach((order) => {
      if (order.orderParts?.length) {
        order.orderParts.forEach((orderPart) => {
          if (!orderPart.isDeleted) {
            orderPartList.push(orderPart);
          }
        });
      }
    });
    const shouldLoadApprovedParts =
      caseSettingsState.case!.isOffline &&
      caseInfo!.approvedCalculationId != null &&
      caseInfo!.approvedCalculationId !== calculationHistoryId;

    const [
      currencyLIst,
      caseCalculation,
      caseCalculationParts,
      partQualityTypes,
      sections,
      caseStatuses,
      ownStockOrganization,
      packageCalculationParts,
      approvedCalculationParts,
      maintenancePackages
    ] = await Promise.all([
      referentialService.Get(currencyRef!.baseUrl),
      calculationHistoryId
        ? caseService.GetCaseCalculationHistoryDetails(calculationHistoryId)
        : ({} as CaseCalculationHistory),
      calculationHistoryId
        ? caseService.GetCaseCalculationParts(calculationHistoryId)
        : ([] as CaseCalculationPart[]),
      referentialService.Get(refQualityPart!.baseUrl),
      caseService.GetCalculationSections(),
      referentialService.Get(refCaseStatus!.baseUrl),
      organizationService.GetSupplierStockOrganization(OrganizationTypeCode.SUPPLIER),
      packageCalculationId
        ? caseService.GetCaseCalculationParts(packageCalculationId)
        : ([] as CaseCalculationPart[]),
      shouldLoadApprovedParts
        ? caseService.GetCaseCalculationParts(caseInfo!.approvedCalculationId!)
        : ([] as CaseCalculationPart[]),
      referentialService.Get(refMaintenancePackage!.baseUrl)
    ]);

    const form = getWorkflowForm(stepFormId);

    const workflowCalculation =
      form!.workflowCalculations.find(
        (item) =>
          item.workflowPartner!.partnerId === appState.appUser!.hoId &&
          (item.userRoleId === appState.appUser!.roleId || R.isNil(item.userRoleId)) &&
          item.caseStatusId === caseSettingsState.case!.caseStatus.caseStatusId
      ) ?? null;

    const workflowOptimization = form!.workflowOptimizations.filter(
      (item) =>
        item.workflowPartner!.partnerId === appState.appUser!.hoId &&
        (item.userRoleId === appState.appUser!.roleId || R.isNil(item.userRoleId)) &&
        item.caseStatusId === caseSettingsState.case!.caseStatus.caseStatusId
    );

    let carClassRentPrices: CarClassRentPrice[] = [];
    let rentVehiclePrices: VehicleDto[] = [];
    if (!R.isNil(casee) && !R.isNil(casee.caseVehicle)) {
      const carClassId = casee.caseVehicle.carClassId;
      if (!R.isNil(carClassId)) {
        carClassRentPrices = await caseService.GetCarClassRentPriceByClassId(carClassId);
      }

      if (caseSettingsState.caseSettings!.workflow.isPrcIntegration) {
        rentVehiclePrices = await caseService.FleetSearchVehicle(
          casee.caseVehicle.vin!,
          casee.caseVehicle.modelId!
        );

        if (!rentVehiclePrices.length) {
          enqueueSnackbar(
            translatorService.Tranlate(
              'NO_CAR_CLASS_FOUNDED_IN_FLEET',
              'Nu s-a gasit masina/clasa in flota!'
            ),
            {
              variant: 'warning'
            }
          );
        }
      }

      const brand = VehicleHelper.GetBrand(caseSettingsState.case, null);
      if (MakeException.includes(brand.toUpperCase())) {
        for (const cp of caseCalculationParts) {
          if (cp.calculationSection!.code === CalculationSectionCodeEnum.PART) {
            const trimmedNumber = cp.guideNumber.replace(/^0+/, '');
            cp.guideNumber = _.cloneDeep(trimmedNumber);
          }
        }
      }
    }

    if (caseSettingsState.caseSettings!.workflow.useRoundUp) {
      for (const cp of caseCalculationParts) {
        if (cp.calculationSection!.code === CalculationSectionCodeEnum.PART) {
          const result = Math.ceil(cp.pieceNr);
          cp.pieceNr = _.cloneDeep(result);
        }
      }
    }
    const workflowSuppliersList = caseSettingsState.caseSettings!.workflow.workflowSuppliers;
    caseCalculation.currency = currencyLIst.find(
      (item) => item.code === caseCalculation.currencyCode
    )!;

    if (orderPartList.length === 0 && approvedCalculationParts.length > 0) {
      orderPartList.push(...convertCalculationPartsToOrderParts(approvedCalculationParts));
    }

    const { caseCalculationPartsCopy, calculationPartsOrdered } = buildOrderedCalculationParts(
      caseCalculationParts,
      orderPartList,
      packageCalculationId,
      workflowCalculation?.useMaintenanceOperationsTransfer ?? false
    );

    caseCalculationPartsCopy.forEach((item) => {
      item.alternativeParts = item.alternativeParts ?? [];
    });

    await applyGTSupplierMapping(caseCalculationPartsCopy);

    const usr = caseCalculation.userId
      ? await appUserService.GetUserInfo(caseCalculation.userId)
      : appState.appUser;

    caseCalculation.user = usr;

    const suppliers =
      workflowSuppliersList.length > 0
        ? await organizationService.GetUserOrganizationsByIds(
            Array.from(new Set(workflowSuppliersList.map((item) => item.supplierId)))
          )
        : [];
    caseCalculationPartsCopy
      .filter(
        (item) =>
          item.calculationSection!.code === CalculationSectionCodeEnum.PART && !item.isOrdered
      )
      .forEach((item) => {
        item.isLoading = false;
        item.hasBeenInterogated = false;
        item.isExpanded = true;
      });

    let workflowPartners = [] as OrganizationBranch[];
    let currentWorkflowPartner = {} as OrganizationBranch | null;

    const workflowSectionStepForms =
      caseSettingsState.caseSettings?.workflow.workflowSections
        ?.map((x) => x.workflowSectionSteps)
        .reduce((acc, val) => acc.concat(val), [])
        .map((x) => x.workflowSectionStepForms)
        .reduce((acc, val) => acc.concat(val), []) || [];
    const currentWorkflowContainsOrderFormOrInterogatePartner =
      workflowSectionStepForms.some((x) => x.form.code === FormCode.CASE_ORDER) &&
      (workflowCalculation?.interogatePartner ?? false);

    const hasBillingForm = workflowSectionStepForms.some(
      (x) => x.form.code === FormCode.CASE_EVENT_DATE_FACT
    );

    if (
      caseSettingsState.caseSettings?.workflow.workflowPartners &&
      caseSettingsState.caseSettings?.workflow.workflowPartners.length > 0
    ) {
      const partnerOrganizations = caseSettingsState.caseSettings?.workflow.workflowPartners
        .filter((x) => x.isActive)
        .map((x) => x.partner);
      for (const x of partnerOrganizations) {
        workflowPartners.push({
          id: x!.id,
          organizationId: x!.id,
          organizationName: x!.name,
          organizationAddress: x!.address,
          children: [],
          parentId: x!.parentId,
          path: null,
          label: x?.name + (x?.address ? ' ' + x?.address : ''),
          checked: false
        });
      }
    }

    const parentIds = workflowPartners.filter((x) => !x.parentId).map((x) => x.id);

    const organizationHierarchy = await organizationService.GetOrganizationDescendents(parentIds);

    const organizationBranches =
      organizationHierarchy
        .filter((x) => x.isActive)
        .map((x) => {
          return {
            id: x.id,
            organizationId: x.id,
            organizationName: x.name,
            organizationAddress: x.address,
            children: [],
            parentId: x.parentId,
            path: null,
            label: x.name + (x.address ? ' ' + x.address : ''),
            checked: false
          } as OrganizationBranch;
        }) || [];

    organizationBranches.forEach((x) => {
      workflowPartners.push(x);
    });

    let deliveryOrganizationId: number | null | undefined;
    if (caseSettingsState.case!.caseEvent != null) {
      deliveryOrganizationId = caseSettingsState.case!.caseEvent!.deliveryOrganizationId;
    } else {
      deliveryOrganizationId = appState.appUser?.organizationId;
    }

    if (currentWorkflowContainsOrderFormOrInterogatePartner) {
      const arrMap = workflowPartners.reduce((acc: Record<number, number>, el, i) => {
        acc[el.id] = i;
        return acc;
      }, {} as Record<number, number>);

      const roots = [] as OrganizationBranch[];

      workflowPartners.forEach((el) => {
        if (el.parentId === null || arrMap[el.parentId] == null) {
          roots.push(el);
        } else {
          workflowPartners[arrMap[el.parentId]].children.push(el);
        }
      });

      currentWorkflowPartner =
        roots.find((x) => x.organizationId === deliveryOrganizationId) || null;
      workflowPartners = roots;
      assignObjectPaths(workflowPartners, '');
      if (currentWorkflowPartner) {
        const selectedItemFromTree = findRecursive(
          workflowPartners,
          currentWorkflowPartner?.organizationId
        );
        if (selectedItemFromTree) {
          selectedItemFromTree.checked = true;
        }
      }
    } else {
      currentWorkflowPartner =
        organizationBranches.find((x) => x.organizationId === deliveryOrganizationId) || null;
    }
    if (currentWorkflowPartner == null) {
      currentWorkflowPartner =
        organizationBranches.find((x) => x.organizationId === deliveryOrganizationId) || null;
      if (currentWorkflowPartner != null) {
        currentWorkflowPartner.checked = true;
      }
    }

    setCalculationParts(caseCalculationPartsCopy);
    setState((prev) => ({
      ...prev,
      caseCalculationHistoryDetails: caseCalculation,
      workflowCalculation: workflowCalculation,
      workflowOptimization: workflowOptimization,
      partQualityTypes: partQualityTypes
        .filter((item) => item.isActive === true)
        .sort((a, b) => (a.code || '').localeCompare(b.code || '')),
      maintenancePackages: maintenancePackages.filter((item) => item.isActive === true),
      TVA: TVA,
      supplierList: suppliers.filter((ws) => ws.isActive === true),
      partSection: sections.find((item) => item.code === CalculationSectionCodeEnum.PART)!,
      selectedOrganizationBranch: currentWorkflowPartner || null,
      organizationBranches: workflowPartners || [],
      currentWorkflowContainsOrderFormOrInterogatePartner:
        currentWorkflowContainsOrderFormOrInterogatePartner,
      calculationPartsOrdered: calculationPartsOrdered,
      caseStatuses: caseStatuses,
      carClassRentPrice: carClassRentPrices.length ? carClassRentPrices[0] : null,
      hasBillingForm: hasBillingForm,
      rentVehiclePrices: rentVehiclePrices.length ? rentVehiclePrices[0] : null,
      ownStockOrganization: ownStockOrganization,
      packageCalculationParts: packageCalculationParts,
      orderPartList: orderPartList,
      calculationSectionList: sections,
      isLoading: false
    }));

    checkPackageNotification(caseCalculationPartsCopy, packageCalculationParts);
  };

  const checkPackageNotification = (
    parts: CaseCalculationPart[],
    packageParts: CaseCalculationPart[]
  ) => {
    const pkgParts = packageParts.filter(
      (part) => part.calculationSection!.code === CalculationSectionCodeEnum.PART
    );
    const partParts = parts.filter(
      (part) => part.calculationSection!.code === CalculationSectionCodeEnum.PART
    );
    const matchingParts = partParts.filter((part) =>
      pkgParts.some((pkgPart) => {
        const sameGuideNumber = pkgPart.guideNumber === part.guideNumber;
        const sameName = pkgPart.name === part.name;
        const startsWithPackagePartName = part.name.startsWith(pkgPart.name);
        return (
          (sameGuideNumber && (sameName || startsWithPackagePartName)) ||
          (pkgPart.repairMethod === part.repairMethod &&
            OriginalPartShortNumberException.includes(pkgPart.repairMethod))
        );
      })
    );

    if (matchingParts.length > 0) {
      const combinedMessage = matchingParts
        .map(({ guideNumber, name }) => `\n ${guideNumber} - ${name} `)
        .join(', ');
      enqueueSnackbar(
        translatorService.Tranlate(
          'PARTS_ALREADY_EXIST_IN_PACKAGE',
          'Piesele de mai jos se regasesc deja in pachetul selectat si nu mai pot fi adaugate: '
        ) + combinedMessage,
        { variant: 'warning', style: { whiteSpace: 'pre-line' }, autoHideDuration: 5000 }
      );
    }
  };

  const getWorkflowForm = (id: number): WorkflowSectionStepForm | null => {
    const sections = caseSettingsState.caseSettings!.workflow.workflowSections!;
    for (const section of sections) {
      for (const step of section.workflowSectionSteps) {
        const form = step.workflowSectionStepForms.find((f) => f.id === id);
        if (!R.isNil(form)) {
          return form;
        }
      }
    }

    return null;
  };

  const applyGTSupplierMapping = async (calculationParts: CaseCalculationPart[]) => {
    const organizationId = caseSettingsState.case?.serviceId ?? appState.appUser!.organizationId;
    const brandId = caseSettingsState.case?.caseVehicle?.brandId;
    if (R.isNil(organizationId) || R.isNil(brandId)) {
      return;
    }

    const parts = calculationParts.filter(
      (item) => item.calculationSection!.code === CalculationSectionCodeEnum.PART && !item.isOrdered
    );
    if (parts.length === 0) {
      return;
    }

    const organizations = await organizationService.GetOrganizationByIds({
      organizationIds: [organizationId]
    });
    const organization = organizations[0];
    if (R.isNil(organization) || !organization.useGTSupplierMapping) {
      return;
    }

    await Promise.all(
      parts.map(async (item) => {
        const gtSupplierMappings = await caseService.GetGTSupplierMapping({
          brandId: brandId,
          gtCode: item.guideNumber,
          qualityId: organization.gtMappingQualityId,
          vin: caseSettingsState.case?.caseVehicle?.vin ?? null
        } as GetGTSupplierMappingRequestDto);

        if (gtSupplierMappings.length === 0) {
          return;
        }

        item.guideNumber = gtSupplierMappings[0].associatedCode;
      })
    );
  };

  const assignObjectPaths = (obj: OrganizationBranch[] | OrganizationBranch, stack: string) => {
    // Generic tree walk: obj is either the branches array or a single branch being recursed into.
    const entries = obj as unknown as Record<string, OrganizationBranch>;
    Object.keys(entries).forEach((k) => {
      const node = entries[k];
      if (node && typeof node === 'object' && !Array.isArray(node)) {
        const path = stack ? `${stack}.${k}` : k;
        node.path = path;
        assignObjectPaths(node, path);
      }
    });
  };

  const findRecursive = (
    arr: OrganizationBranch[],
    organizationId: number
  ): OrganizationBranch | null => {
    let result: OrganizationBranch | null = null;
    for (const entry in arr) {
      if (arr[entry].organizationId == organizationId) {
        result = arr[entry];
        break;
      }
      if (arr[entry].children?.length) {
        const partialResult = findRecursive(arr[entry].children, organizationId);
        if (partialResult != null) {
          result = partialResult;
          break;
        }
      }
    }
    return result;
  };

  const onDropDownChange = (currentNode: TreeNode, selectedNodes: TreeNode[]) => {
    const organizationBranches = _.cloneDeep(state.organizationBranches);
    let newSelectionOrganizationBranch: OrganizationBranch | null = null;
    if (state.selectedOrganizationBranch) {
      const oldSelectionOrganizationBranch = findRecursive(
        organizationBranches,
        state.selectedOrganizationBranch.organizationId
      );
      if (oldSelectionOrganizationBranch) {
        oldSelectionOrganizationBranch.checked = false;
      }
    }

    if (selectedNodes.length == 1) {
      const newPartialSelectionOrganizationBranch =
        findRecursive(
          organizationBranches,
          (selectedNodes[0] as unknown as OrganizationBranch)?.organizationId
        ) || null;

      if (newPartialSelectionOrganizationBranch) {
        newPartialSelectionOrganizationBranch.checked = true;
        newSelectionOrganizationBranch = newPartialSelectionOrganizationBranch;
      }
    }

    const interogableParts = getInterogableParts();
    if (interogableParts.some((item) => item.hasBeenInterogated)) {
      enqueueSnackbar(
        translatorService.Tranlate(
          'REINTEROGATE_ALTERANTIVE_PARTS',
          'Adresa de livrare a fost modificată , vă rugăm să reinterogați piesele!'
        ),
        { variant: 'warning', autoHideDuration: 6000 }
      );
    }

    setState((prev) => ({
      ...prev,
      organizationBranches: organizationBranches,
      selectedOrganizationBranch: newSelectionOrganizationBranch
    }));

    setCalculationParts((parts) =>
      parts.map((item) =>
        isInterogablePart(item)
          ? {
              ...revertAlternativeMapping(item),
              alternativeParts: [],
              hasBeenInterogated: false,
              isExpanded: false
            }
          : item
      )
    );
  };

  useEffect(() => {
    const caseSettings = caseSettingsState.caseSettings;
    const caseId = Number.parseInt(params.id);
    if (caseSettings === null || caseSettings === undefined || Number.isNaN(caseId)) {
      return;
    }
    setState((prev) => ({ ...prev, isLoading: true }));
    loadCalculationDetails();
  }, []);

  const getInterogableParts = (): CaseCalculationPart[] => {
    return calculationParts.filter((part) => isInterogablePart(part));
  };

  const resetRecommendations = () => {
    setCalculationParts((parts) =>
      parts.map((p) =>
        isInterogablePart(p)
          ? {
              ...revertAlternativeMapping(p),
              alternativeParts: deselectAllVariants(p.alternativeParts)
            }
          : p
      )
    );
  };

  const scrollToSelectedItems = () => {
    [...calcPartsRef.current].reverse().forEach((part) => {
      const recommended = part.alternativeParts.find(
        (a) => a.isSelectedAlternativePart && !a.isChild
      );
      if (recommended && rowRefs.current[recommended.id]) {
        rowRefs.current[recommended.id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  };

  const sendNotificationToSupport = async (part: CaseCalculationPart) => {
    try {
      const request = {
        caseId: caseSettingsState.case!.id,
        organizationOwnerId: caseSettingsState.case!.organizationOwnerId,
        template: 'GTM_NOTIFY_SUPPORT',
        originalPart: {
          oeCode: part.guideNumber,
          name: part.name,
          price: part.originalPrice,
          quality: part.carPartCategory?.displayName ?? '',
          comment: part.supportComment ?? ''
        } as OriginalPart
      } as PartNotifySuppportRequest;
      await caseService.SendPartNotifySupport(request);
      enqueueSnackbar(
        translatorService.Tranlate(
          'NOTIFICATION_SEND_SUCCCESS_MSG',
          'Notificarea catre support a fost trimisa cu succces!'
        ),
        { variant: 'success' }
      );
    } catch (error) {
      enqueueSnackbar(translatorService.Tranlate('ERROR_MSG', 'Eroare'), { variant: 'error' });
    }
  };

  const interogateAll = async () => {
    getInactiveWorkflowSuppliers();
    getNonStockSuppliers();
    try {
      const parts = getInterogableParts();

      await Promise.all(
        parts.map(async (cp) => {
          await applyOilMapping(cp);
          await interogate(cp);
        })
      );
      enqueueSnackbar(
        translatorService.Tranlate(
          'ALT_CALC_QUERIED_ALL',
          'Toate piesele au fost interogate la furnizori'
        ),
        { variant: 'success' }
      );
    } catch (error) {
      enqueueSnackbar(translatorService.Tranlate('ERROR_MSG', 'Eroare'), { variant: 'error' });
    }
  };

  const resetAlternatives = () => {
    setCalculationParts((parts) =>
      parts.map((p) => ({
        ...p,
        isLoading: false,
        hasBeenInterogated: false,
        isExpanded: false,
        alternativeParts: deselectAllVariants(p.alternativeParts)
      }))
    );
  };

  const deletePart = (partId: number) => {
    setCalculationParts((parts) => parts.filter((p) => p.id !== partId));
  };

  const addAlternativePart = (partId: number, alternativePart: CaseCalculationAlternativePart) => {
    setCalculationParts((parts) =>
      updatePart(parts, partId, (part) => ({
        ...part,
        isExpanded: true,
        hasBeenInterogated: true,
        alternativeParts: [
          ...part.alternativeParts,
          makeAlternativePart(uuidToNumber(), part.id, alternativePart)
        ]
      }))
    );
  };

  const importOriginalParts = (inputs: CaseCalculationPart[]) => {
    setCalculationParts((parts) => [
      ...parts,
      ...inputs.map((input) => ({
        ...makeCalculationPart(-uuidToNumber(), input),
        calculationSectionId: state.partSection!.id,
        calculationSection: state.partSection
      }))
    ]);
  };

  const importAlternativeParts = (alternativeParts: CaseCalculationAlternativePart[]) => {
    setCalculationParts((parts) =>
      parts.map((part) => {
        const matches = alternativeParts.filter((a) => a.originalPartId === part.id);
        if (!matches.length) return part;
        return {
          ...part,
          isExpanded: true,
          hasBeenInterogated: true,
          alternativeParts: [
            ...part.alternativeParts,
            ...matches.map((a) => makeAlternativePart(uuidToNumber(), part.id, a))
          ]
        };
      })
    );
  };

  const setPartsLoading = (isLoading: boolean) => {
    return (parts: CaseCalculationPart[]) => {
      return parts.map((item) => (isInterogablePart(item) ? { ...item, isLoading } : item));
    };
  };

  const getPriceForInsurer = (item: VehicleDto | null): number => {
    if (item === null) return 0;
    if (R.isNil(caseSettingsState.case!.caseEvent)) return 0;
    const insurerName = caseSettingsState.case!.caseEvent!.insurerName;
    if (R.isNil(insurerName) || insurerName === '') return 0;
    const insurer = _.find(
      insurerList,
      (i) => i.displayName.toLowerCase() === insurerName.toLowerCase()
    );
    if (R.isNil(insurer)) return 0;
    const insurerCode = (insurer.code?.toLowerCase() ?? '') + 'Pret';
    if (!Object.prototype.hasOwnProperty.call(item, insurerCode)) return 0;
    const value = item[insurerCode as keyof VehicleDto];
    if (typeof value === 'undefined' || value === null) return 0;
    const numericValue = parseFloat(value.toString());
    return isNaN(numericValue) ? 0 : numericValue;
  };

  const checkIfOptimIsPossible = (): boolean => {
    const interogableParts = getInterogableParts();
    return _.findIndex(interogableParts, (part) => !part.hasBeenInterogated) === -1;
  };

  const filterAlternativePartsByDeliveryTime = (
    alternativeParts: CaseCalculationAlternativePart[]
  ) =>
    alternativeParts.filter(
      (item) =>
        Number(item.deliveryTime) <= (state.workflowCalculation?.defaultMaxDeliveryTime ?? 0) ||
        (item.deliveryTime ?? '').match(/^[0-9]+$/) === null
    );

  const filterAlternativePartsByPrice = (alternativeParts: CaseCalculationAlternativePart[]) => {
    if (state.workflowCalculation!.byAquisitionPrice) {
      const min = alternativeParts.reduce((prev, curr) =>
        prev.alternativeNetPrice! < curr.alternativeNetPrice! ? prev : curr
      ).alternativeNetPrice;
      return alternativeParts.filter((item) => item.alternativeNetPrice === min);
    } else {
      const min = alternativeParts.reduce((prev, curr) =>
        prev.alternativePrice < curr.alternativePrice ? prev : curr
      ).alternativePrice;
      return alternativeParts.filter((item) => item.alternativePrice === min);
    }
  };

  const filterAlternativePartsByQuality = (alternativeParts: CaseCalculationAlternativePart[]) => {
    const qualities = CarPartQualityRatings.filter((elem) =>
      alternativeParts.find(
        ({ alternativePartQuality }) => elem.CarPartQualityTypeCode === alternativePartQuality!.code
      )
    );
    const bestQuality = qualities.sort((a, b) => b.Rating - a.Rating)[0];
    return alternativeParts.filter(
      (item) => item.alternativePartQuality!.code === bestQuality.CarPartQualityTypeCode
    );
  };

  const optimizeByBestPrice = () => {
    resetRecommendations();
    const interogableParts = getInterogableParts();
    interogableParts.forEach((calculationOriginalPart) => {
      let altParts = getParentParts(calculationOriginalPart, true);
      if (altParts.length > 0) {
        altParts = filterAlternativePartsByDeliveryTime(altParts);
        if (altParts.length > 0) {
          altParts = filterAlternativePartsByPrice(altParts);
          if (altParts.length > 1) {
            altParts = filterAlternativePartsByQuality(altParts);
            if (altParts.length > 1) {
              altParts = [altParts.sort((a, b) => compareByExpectedDelivery(a, b))[0]];
            }
          }
        }
      }
      if (altParts.length > 0) {
        const altPart = altParts[0];
        setTimeout(() => {
          handleAlternativePartSelect(altPart.id, calculationOriginalPart.id, altPart, true);
        }, 0);
      }
    });
    // Queued after the per-part select timeouts above, so every selection has committed first.
    setTimeout(() => scrollToSelectedItems(), 0);
  };

  const optimizeByBestQuality = () => {
    resetRecommendations();
    const interogableParts = getInterogableParts();
    interogableParts.forEach((calculationOriginalPart) => {
      let altParts = getParentParts(calculationOriginalPart, true);
      if (altParts.length > 0) {
        altParts = filterAlternativePartsByDeliveryTime(altParts);
        if (altParts.length > 0) {
          altParts = filterAlternativePartsByQuality(altParts);
          if (altParts.length > 1) {
            altParts = filterAlternativePartsByPrice(altParts);
            if (altParts.length > 1) {
              altParts = [altParts.sort((a, b) => compareByExpectedDelivery(a, b))[0]];
            }
          }
        }
      }
      if (altParts.length > 0) {
        const altPart = altParts[0];
        setTimeout(() => {
          handleAlternativePartSelect(altPart.id, calculationOriginalPart.id, altPart, true);
        }, 0);
      }
    });
    // Queued after the per-part select timeouts above, so every selection has committed first.
    setTimeout(() => scrollToSelectedItems(), 0);
  };

  const optimize = async (includeChild: boolean) => {
    resetRecommendations();
    setCalculationParts(setPartsLoading(true));

    const offerLines: OfferLine[] = [];
    const parts = getInterogableParts();
    for (const cp of parts) {
      const parentParts = getParentParts(cp, includeChild);
      if (parentParts.length) {
        offerLines.push({
          originalPartId: cp.id,
          guideNumber: cp.guideNumber,
          quantity: cp.pieceNr,
          offerParts: parentParts.map(
            (part) =>
              ({
                alternativePartCode: part.alternativePartCode,
                alternativePartName: part.name,
                alternativePrice: part.alternativePrice,
                deliveryTime: part.expectedDeliveryDate,
                availableQuantity: part.availableQuantity ?? 99999,
                rowId: part.id
              } as OfferPart)
          )
        } as OfferLine);
      }
    }

    const getBestOfferRequest = {
      caseId: caseSettingsState.case!.id,
      price: caseSettingsState.caseSettings!.workflow.isPrcIntegration
        ? getPriceForInsurer(state.rentVehiclePrices)
        : state.carClassRentPrice?.price ?? 0,
      offerLines
    } as Offer;

    const bestOfferResult = await caseService.GetBestOffer(getBestOfferRequest);
    for (const cp of parts) {
      const bestOfferLine = _.find(
        bestOfferResult.offerLines,
        (line) => line.originalPartId === cp.id
      );
      if (!bestOfferLine) continue;
      if (bestOfferLine.offerParts.length) {
        const bestOfferPart = bestOfferLine.offerParts[0];
        const alternativePart = cp.alternativeParts.find((item) => item.id === bestOfferPart.rowId);
        if (alternativePart) {
          const alt = includeChild ? alternativePart : { ...alternativePart, bundleNo: null };
          // Deferred like best price/quality so the resetRecommendations() update flushes first.
          setTimeout(() => handleAlternativePartSelect(alt.id, cp.id, alt, true), 0);
        }
      } else {
        enqueueSnackbar(
          translatorService.Tranlate('NO_OPTIM', 'Nu s-a gasit niciun optim pentru:') +
            ' ' +
            cp.guideNumber,
          { variant: 'warning' }
        );
      }
    }

    setCalculationParts(setPartsLoading(false));
    setTimeout(() => scrollToSelectedItems(), 0);
  };

  const [isOptimizingLoading, setIsOptimizingLoading] = useState(false);
  const [isApplyingOptimalLoading, setIsApplyingOptimalLoading] = useState(false);

  const optimizeAllParts = async (includeChild: boolean) => {
    setIsOptimConfirmOpen(false);
    setIsOptimizingLoading(true);
    try {
      await optimize(includeChild);
    } catch (error) {
      enqueueSnackbar(translatorService.Tranlate('ERROR_MSG', 'Eroare'), { variant: 'error' });
      setCalculationParts(setPartsLoading(false));
    } finally {
      setIsOptimizingLoading(false);
    }
  };

  const validatePieceNrOrginalParts = (): boolean => {
    const interogableParts = getInterogableParts();
    const invalidIds = interogableParts.filter((part) => !part.pieceNr).map((part) => part.id);
    if (invalidIds.length) {
      setCalculationParts((parts) =>
        parts.map((p) => (invalidIds.indexOf(p.id) !== -1 ? { ...p, unitsError: true } : p))
      );
      enqueueSnackbar(
        translatorService.Tranlate(
          'VALIDATION_QTY_ZERO_ERROR_MSG',
          'Cantitatea comandata trebuie sa fie mai mare decat 0!'
        ),
        { variant: 'warning', autoHideDuration: 5000 }
      );
      return true;
    }
    return false;
  };

  const validateOrginalParts = (): boolean => {
    const useRoundUp = caseSettingsState.caseSettings!.workflow.useRoundUp;
    const interogableParts = getInterogableParts();
    const invalidIds = interogableParts
      .filter(
        (part) =>
          !(!useRoundUp && OriginalPartShortNumberException.includes(part.repairMethod)) &&
          !Number.isInteger(part.pieceNr)
      )
      .map((part) => part.id);
    if (invalidIds.length) {
      setCalculationParts((parts) =>
        parts.map((p) => (invalidIds.indexOf(p.id) !== -1 ? { ...p, unitsError: true } : p))
      );
      enqueueSnackbar(
        translatorService.Tranlate(
          'DECIMAL_ERROR_MSG',
          'Cantitatea comandata trebuie sa fie un numar intreg fara zecimale'
        ),
        { variant: 'warning' }
      );
      return true;
    }
    return false;
  };

  const isDialogOpenConfirmationHandler = (func: () => void) => {
    if (state.currentWorkflowContainsOrderFormOrInterogatePartner) {
      setGatedAction(() => func);
      setIsOrgPartnerConfirmOpen(true);
    } else {
      func();
    }
  };

  const openOptimizeConfirmation = () => {
    if (validatePieceNrOrginalParts()) {
      return;
    }
    if (validateOrginalParts()) {
      return;
    }
    const interogableParts = getInterogableParts();
    const hasChildItem = interogableParts.some((part) =>
      part.alternativeParts.some((alternativePart) => alternativePart.isChild)
    );
    if (hasChildItem) {
      setIsOptimConfirmOpen(true);
    } else {
      optimizeAllParts(true);
    }
  };

  const applyOptimal = async () => {
    resetRecommendations();
    setCalculationParts(setPartsLoading(true));
    setIsApplyingOptimalLoading(true);

    const parts = getInterogableParts().filter((p) => p.alternativeParts.length > 0);
    if (!parts.length) {
      setCalculationParts(setPartsLoading(false));
      setIsApplyingOptimalLoading(false);
      return;
    }

    const request: GetPackagePartSelectionsRequestDto = {
      parts: parts.map((p) => ({
        id: p.id,
        name: p.name,
        guideNumber: p.guideNumber,
        pieceNr: p.pieceNr,
        alternativePartList: getParentParts(p, false).map((a) => ({
          id: a.id,
          name: a.name,
          alternativeGuideNumber: a.alternativeGuideNumber,
          alternativePrice: a.alternativePrice,
          producer: a.producer,
          supplierId: a.supplierId,
          availableQuantity: a.availableQuantity,
          expectedDeliveryDate: a.expectedDeliveryDate
        }))
      })),
      countryCode: appState.appUser?.organization?.country?.code ?? CountryCodeEnum.RO.toString()
    };

    try {
      const results = await caseService.GetPackagePartSelections(request);

      let optimizedCount = 0;
      for (const cp of parts) {
        const selections = results.find((r) => r.id === cp.id)?.selections ?? [];
        const rows = selections.filter((s) => s.alternativePart != null);

        const missingPackages = selections
          .filter((s) => s.alternativePart == null)
          .map((s) => s.package.displayName);
        if (missingPackages.length) {
          enqueueSnackbar(
            translatorService.Tranlate('NO_OPTIM', 'Nu s-a gasit niciun optim pentru:') +
              ' ' +
              cp.guideNumber +
              ' — ' +
              missingPackages.join(', '),
            { variant: 'warning' }
          );
        }
        if (!rows.length) {
          continue;
        }
        optimizedCount++;

        const recommendedRow = rows.find((s) => s.isServiceRecomandation) ?? rows[0];
        const recommendedAlt = cp.alternativeParts.find(
          (a) => a.id === recommendedRow.alternativePart!.id
        );
        const variantRows = rows.filter((s) => s.alternativePart!.id !== recommendedAlt?.id);
        // Favorite first, then the rest on top of the committed state.
        setTimeout(() => {
          if (recommendedAlt) {
            // resetVariants: sterge selectia butonului anterior; variantele proprii se re-adauga mai jos.
            handleAlternativePartSelect(recommendedAlt.id, cp.id, recommendedAlt, true);
          }
          if (variantsEnabled) {
            variantRows.forEach((s) => {
              const alt = cp.alternativeParts.find((a) => a.id === s.alternativePart!.id);
              if (alt) {
                toggleAlternativePartSelection(cp.id, alt);
              }
            });
          }
        }, 0);
      }

      if (optimizedCount > 0) {
        enqueueSnackbar(
          `${translatorService.Tranlate('ALT_CALC_OPTIMIZED_FOR', 'Selecție optimizată pentru')} ` +
            `${optimizedCount} ${translatorService.Tranlate(
              'ALT_CALC_PARTS',
              'piese'
            )}: OE / Premium / Buget.`,
          { variant: 'success' }
        );
      }
    } catch (error) {
      enqueueSnackbar(translatorService.Tranlate('ERROR_MSG', 'Eroare'), { variant: 'error' });
    }

    setCalculationParts(setPartsLoading(false));
    setIsApplyingOptimalLoading(false);
    setTimeout(() => scrollToSelectedItems(), 0);
  };

  const addCalculationPart = () => {
    setCalculationPartFormTypeAdd(true);
    setNewCalculationPart({
      ...emptyCalculationPartDraft(),
      originalPrice: null,
      pieceNr: null
    } as unknown as CaseCalculationPart);
    setIsAddDialogOpen(true);
  };

  const copyCalculationPart = (part: CaseCalculationPart) => {
    setCalculationPartFormTypeAdd(false);
    // A copied row starts fresh — blank every alternate-mapped field.
    setNewCalculationPart({
      ...part,
      pieceNr: null,
      alternativeParts: [],
      alternativeGuideNumber: '',
      alternativePartCode: '',
      alternativePartQuality: null,
      alternativePartQualityId: null,
      alternativePrice: null,
      alternativePartDiscount: null,
      alternativePartName: null,
      alternativePartStockLocation: null,
      alternativePartStockQuantity: null,
      alternativePartRemarks: null,
      minQty: 0,
      packing: null,
      bundleItem: null,
      bundleNo: null,
      bundleQuantity: null,
      alternativeNetPrice: null,
      alternativePriceWithoutDiscount: null
    });
    setIsAddDialogOpen(true);
  };

  const saveNewCalculationPart = (formPart: CaseCalculationPart) => {
    const isNew =
      state.orderPartList.length > 0 &&
      !state.orderPartList.some((op) => isPartAlreadyOrdered(op, formPart, packageCalculationId));
    setCalculationParts((parts) => [
      ...parts,
      {
        ...formPart,
        id: -uuidToNumber(),
        amount: 0,
        calculationSectionId: state.partSection!.id,
        calculationSection: state.partSection,
        isNew: isNew,
        isLoading: false,
        hasBeenInterogated: false
      }
    ]);
    enqueueSnackbar(translatorService.Tranlate('SUCCES_MSG', 'OK'), { variant: 'success' });
  };

  const copyCalculationPartForm = (formPart: CaseCalculationPart) => {
    setCalculationParts((parts) => [
      ...parts,
      { ...formPart, id: -uuidToNumber(), amount: 0, isLoading: false, hasBeenInterogated: false }
    ]);
    enqueueSnackbar(translatorService.Tranlate('SUCCES_MSG', 'OK'), { variant: 'success' });
  };

  const submitCalculationPart = (formPart: CaseCalculationPart) => {
    if (calculationPartFormTypeAdd) saveNewCalculationPart(formPart);
    else copyCalculationPartForm(formPart);
    setIsAddDialogOpen(false);
  };

  const getPartsOrganizationId = (): number | null => appState.appUser!.organizationId;

  const setCaseCalculationPartInterogations = (part: CaseCalculationPart) => {
    part.caseCalculationPartInterogations = [
      { interogationList: JSON.stringify(part.alternativeParts ?? []) }
    ];
  };

  const setSuggestedVariantsJson = (part: CaseCalculationPart) => {
    // Stamp the service recommendation so the fleet app reads it directly.
    const selected = part.alternativeParts
      .filter((a) => a.isVariantSelected)
      .map((a) => ({ ...a, isServiceRecommendation: a.isSelectedAlternativePart }));
    part.suggestedVariantsJson = selected.length ? JSON.stringify(selected) : null;
  };

  const save = async () => {
    if (state.hasBillingForm && state.selectedOrganizationBranch?.organizationId == null) {
      enqueueSnackbar(
        translatorService.Tranlate(
          'ALTERNATIVE_CALCULATION_NO_DELIVERY_ADDRESS',
          'Va rugam selectati adresa de livrare!'
        ),
        { variant: 'warning', autoHideDuration: 6000 }
      );
      return;
    }

    try {
      const interogableParts = getInterogableParts();

      // Variants mode: block the save when an offered part carries fewer than the minimum variants.
      if (variantsEnabled) {
        const belowMin = interogableParts.filter((part) => isBelowMinVariants(part));
        if (belowMin.length) {
          const message =
            belowMin.length === 1
              ? translatorService.Tranlate(
                  'ALT_CALC_MIN_VARIANTS_WARNING_ONE',
                  'Recomandat minim 3 variante pe piesa:'
                )
              : translatorService.Tranlate(
                  'ALT_CALC_MIN_VARIANTS_WARNING',
                  'Recomandat minim 3 variante pe piesele:'
                );
          enqueueSnackbar(message + ' ' + belowMin.map((p) => p.guideNumber).join(', '), {
            variant: 'warning',
            autoHideDuration: 6000
          });
          return;
        }
      }

      if (!caseSettingsState.case!.isOffline) {
        if (state.workflowCalculation!.allPartsHasBeenInterogated) {
          if (!checkIfOptimIsPossible()) {
            enqueueSnackbar(
              translatorService.Tranlate(
                'ALTERNATIVE_CALCULATION_WITHOUT_ALL_PARTS_INTEROGATED',
                'Va rugam interogati toate piesele!'
              ),
              { variant: 'warning', autoHideDuration: 6000 }
            );
            return;
          }
        } else if (
          interogableParts.filter((item) => !R.isNil(item.alternativePartQualityId)).length === 0 &&
          !isConfirm
        ) {
          enqueueSnackbar(
            translatorService.Tranlate(
              'ALTERNATIVE_CALCULATION_WITHOUT_INTEROGATE_PARTS',
              'O calculatie alternativa nu poate fi salvata daca nu are niciun reper interogat!'
            ),
            { variant: 'warning', autoHideDuration: 6000 }
          );
          return;
        }
      }

      const { validated, errorString, decimalError, descriptionError, zeroPieceNrError } =
        validateSelectedParts(interogableParts);

      // Apply the refreshed per-part flags so the offending Bucăți / Descriere inputs turn red.
      const validatedById = new Map(validated.map((p) => [p.id, p]));
      setCalculationParts((parts) => parts.map((p) => validatedById.get(p.id) ?? p));

      const ownStock = state.workflowCalculation!.serviceOwnStock && !!state.ownStockOrganization;
      const zeroPriceError =
        ownStock && calculationParts.some((part) => isZeroPriceOwnStockPart(part));

      if (errorString) {
        enqueueSnackbar(errorString, { variant: 'warning', style: { whiteSpace: 'pre-line' } });
        return;
      }
      if (decimalError) {
        enqueueSnackbar(
          translatorService.Tranlate(
            'DECIMAL_ERROR_MSG',
            'Cantitatea comandata trebuie sa fie un numar intreg fara zecimale'
          ),
          { variant: 'warning' }
        );
        return;
      }
      if (descriptionError) {
        enqueueSnackbar(
          translatorService.Tranlate(
            'ALTERNATIVE_PART_DESCRIPTION_ERROR_MSG',
            'Va rugam completati descrierea piesei alternative!'
          ),
          { variant: 'warning' }
        );
        return;
      }
      if (zeroPieceNrError) {
        enqueueSnackbar(
          translatorService.Tranlate(
            'VALIDATION_QTY_ZERO_ERROR_MSG',
            'Cantitatea comandata trebuie sa fie mai mare decat 0!'
          ),
          { variant: 'warning', autoHideDuration: 5000 }
        );
        return;
      }
      if (zeroPriceError) {
        enqueueSnackbar(
          translatorService.Tranlate('ZERO_PRICE_ERROR_MSG', 'Pretul trebuie sa fie diferit de 0!'),
          { variant: 'warning' }
        );
        // Re-enable and flag the zero-price rows for correction.
        setCalculationParts((parts) =>
          parts.map((p) =>
            isZeroPriceOwnStockPart(p) ? { ...p, editOriginalPrice: true, priceError: true } : p
          )
        );
        return;
      }

      if (
        interogableParts.filter((item) => R.isNil(item.alternativePartQualityId)).length !== 0 &&
        !isConfirm
      ) {
        setIsModifiedConfirmOpen(true);
        return;
      }

      setExecuting(true);

      let caseCalculationHistoryId = calculationHistoryId;
      if (!caseCalculationHistoryId) {
        caseCalculationHistoryId = (
          await caseService.CreateCalculationHistory({
            caseId: caseSettingsState.case!.id,
            formId: state.workflowCalculation?.workflowSectionStepFormId,
            calculationId: null,
            calculationTypeId: 2,
            workflowCalculationId: state.workflowCalculation!.id,
            partsLocationOrganizationId: getPartsLocationOrganizationId(),
            partsOrganizationId: getPartsOrganizationId(),
            organizationId: appState.appUser!.organizationId,
            organizationName: appState.appUser!.organization!.name,
            manipulationPercent: manipulationTariff!.value,
            hasUnorderedParts: interogableParts.length > 0
          } as CalculationRequest)
        ).id;
      }

      const partsWithInterogations = _.cloneDeep(calculationParts);
      partsWithInterogations
        .filter((part) => isInterogablePart(part))
        .forEach((part) => {
          setCaseCalculationPartInterogations(part);
          if (variantsEnabled) {
            setSuggestedVariantsJson(part);
          }
        });

      let calculationPartsToSave = _.cloneDeep(
        partsWithInterogations.concat(getPackageFromCalculationParts(partsWithInterogations))
      );
      if (state.workflowCalculation!.serviceOwnStock && state.ownStockOrganization) {
        calculationPartsToSave.forEach((item) => {
          if (
            item.calculationSection!.code === CalculationSectionCodeEnum.PART &&
            !item.isOrdered &&
            !item.supplierId
          ) {
            item.supplierId = state.ownStockOrganization!.id;
            item.isOwnStock = true;
          }
        });
      }
      calculationPartsToSave = calculationPartsToSave.concat([...state.packageCalculationParts]);

      await caseService.UpdateCaseCalculationParts(
        {
          parts: calculationPartsToSave,
          comment: state.caseCalculationHistoryDetails.comment,
          offerNumber: state.caseCalculationHistoryDetails.offerNumber,
          partsLocationOrganizationId: getPartsLocationOrganizationId(),
          partsOrganizationId: getPartsOrganizationId(),
          organizationId: appState.appUser!.organizationId,
          organizationName: appState.appUser!.organization!.name,
          isUpdate: false,
          manipulationPercent: manipulationTariff!.value,
          additionInsurer: null,
          formId: state.workflowCalculation!.workflowSectionStepFormId,
          interogatePartner: state.currentWorkflowContainsOrderFormOrInterogatePartner,
          hasUnorderedParts: interogableParts.length > 0,
          excludeGTMAddition:
            (appState.appUser!.organization!.isGtmManaged &&
              caseSettingsState?.caseSettings?.workflow?.isCarfixIntegration) ??
            false
        },
        caseCalculationHistoryId
      );

      if (appState.appUser!.organization!.changeCaseStatusBasedCalculation) {
        const status = state.caseStatuses.find((cs) => cs.code === CaseStatusCodeEnum.AM);
        const currentStatus = await caseService.ChangeCaseStatus({
          caseId: caseSettingsState.case!.id,
          statusId: status!.id
        } as ChangeCaseStatusRequest);
        dispatch(CaseSettingsActionCreators.SetCaseStatus(currentStatus));
      }

      enqueueSnackbar(translatorService.Tranlate('SUCCES_MSG', 'OK'), { variant: 'success' });
      setIsConfirm(false);
      setExecuting(false);
      closeDialog();
    } catch (err) {
      console.error(err);
      enqueueSnackbar(translatorService.Tranlate('ERROR_MSG', 'Eroare'), { variant: 'error' });
      setExecuting(false);
    }
  };

  const confirmSaveModified = () => {
    setIsModifiedConfirmOpen(false);
    setIsConfirm(true);
  };

  const closeModifiedDialog = () => {
    setIsModifiedConfirmOpen(false);
    setIsConfirm(false);
  };

  useEffect(() => {
    if (isConfirm) {
      save();
    }
  }, [isConfirm]);

  const logStockOverrideConfirm = (
    part: CaseCalculationPart,
    alternativePart: CaseCalculationAlternativePart
  ) => {
    const user = appState.appUser
      ? { id: appState.appUser.id, email: appState.appUser.email }
      : null;
    captureStockExceededConfirm(user, {
      caseId: caseSettingsState.case?.id ?? null,
      partName: part.name ?? null,
      partCode: part.guideNumber ?? null,
      alternativePartCode: alternativePart.alternativeGuideNumber ?? null,
      selectedQuantity: part.pieceNr,
      availableStock: alternativePart.availableQuantity ?? null
    });
  };

  const calculationActions: CalculationActions = {
    registerRow,
    toggleExpand,
    interogate,
    interogateAlternativeParts: (part: CaseCalculationPart) =>
      isDialogOpenConfirmationHandler(() => interogateAlternativeParts(part)),
    interogateAll: () => isDialogOpenConfirmationHandler(interogateAll),
    resetAlternatives,
    toggleSelect: toggleAlternativePartSelection,
    setRecommended: (partId: number, alternativePart: CaseCalculationAlternativePart) =>
      handleAlternativePartSelect(alternativePart.id, partId, alternativePart),
    clearRecommendation,
    unselectPart,
    unselectAll,
    deletePart,
    sendNotificationToSupport,
    addAlternativePart,
    importOriginalParts,
    importAlternativeParts,
    updateGuideNumber,
    updatePieceNr,
    updateOriginalPrice,
    updateCarPartCategory,
    updateAlternativePartDescription,
    canOptimize: checkIfOptimIsPossible,
    openOptimize: openOptimizeConfirmation,
    optimizeByBestPrice,
    optimizeByBestQuality,
    applyOptimal,
    isOptimizingLoading,
    isApplyingOptimalLoading,
    addCalculationPart,
    copyCalculationPart,
    logStockOverrideConfirm
  };

  const maintenancePackage =
    state.maintenancePackages.find(
      (p) => p.id === state.caseCalculationHistoryDetails.maintenancePackageId
    ) ?? null;

  const interogableParts = getInterogableParts();

  return (
    <Dialog open onClose={onClose} fullScreen fullWidth>
        <AlternativeCalculationHeader
          onClose={onClose}
          title={translatorService.Tranlate('ALT_CALC_TITLE', 'Detalii calculație alternativă')}
          showReference={true}
        />
        <DialogContent className={c.pageBg} style={{ padding: 0 }}>
          {state.isLoading ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, width: '100%' }}>
                <ScaleLoader color={'var(--primary)'} loading={true} />
              </div>
            </div>
          ) : (
            <CalculationFlagsProvider
              variantsEnabled={alternativePartVariantsEnabled}
              isOffline={isOffline}
            >
              <AlternativeCalculationContent
                parts={interogableParts}
                orderedParts={state.calculationPartsOrdered}
                currency={state.caseCalculationHistoryDetails.currency ?? null}
                maintenancePackage={maintenancePackage}
                totals={totals}
                deliveryAddresses={state.organizationBranches}
                actions={calculationActions}
                partQualityTypes={state.partQualityTypes}
                supplierList={state.supplierList}
                nonStockSuppliers={state.nonStockSuppliers}
                isAddDialogOpen={isAddDialogOpen}
                isAddDialog={calculationPartFormTypeAdd}
                newCalculationPart={newCalculationPart}
                onCloseAddDialog={() => setIsAddDialogOpen(false)}
                onSubmitCalculationPart={submitCalculationPart}
                onClose={onClose}
                onSave={save}
                disabled={executing}
                isLoading={executing}
                onDropDownChange={onDropDownChange}
                calculationHistoryId={calculationHistoryId}
                workflowCalculation={state.workflowCalculation}
                workflowOptimization={state.workflowOptimization}
                currentWorkflowContainsOrderFormOrInterogatePartner={
                  state.currentWorkflowContainsOrderFormOrInterogatePartner
                }
                orderList={orderList}
                caseInfo={caseInfo}
                manipulationTariff={manipulationTariff}
                insurerList={insurerList}
                serviceId={serviceId}
                additionTariff={additionTariff}
                discountTariff={discountTariff}
                packageCalculationId={packageCalculationId}
              />
            </CalculationFlagsProvider>
          )}
        </DialogContent>

        <ConfirmDialog
          open={isOptimConfirmOpen}
          title={translatorService.Tranlate(
            'OPTIM_PARTS_DIALOG_TITLE_SHORT',
            'Componentă suplimentară'
          )}
          message={translatorService.Tranlate(
            'OPTIM_PARTS_DIALOG_TITLE',
            'Atentie! Exista o componenta suplimentara. Doriti adaugarea acesteia ?'
          )}
          confirmLabel={translatorService.Tranlate('WORKFLOW_DOCUMENTS_TYPE_TAB_YES', 'Da')}
          cancelLabel={translatorService.Tranlate('WORKFLOW_DOCUMENTS_TYPE_TAB_NO', 'Nu')}
          onConfirm={() => optimizeAllParts(true)}
          onClose={() => optimizeAllParts(false)}
        />

        <ConfirmDialog
          open={isModifiedConfirmOpen}
          title=""
          message={translatorService.Tranlate(
            'ERROR_MSG_NO_PARTS_SELECTED',
            'Aveti repere neoptimizate. Doriti sa salvati?'
          )}
          confirmLabel={translatorService.Tranlate('WORKFLOW_DOCUMENTS_TYPE_TAB_YES', 'Da')}
          cancelLabel={translatorService.Tranlate('WORKFLOW_DOCUMENTS_TYPE_TAB_NO', 'Nu')}
          onConfirm={confirmSaveModified}
          onClose={closeModifiedDialog}
        />

        <ConfirmDialog
          open={isOrgPartnerConfirmOpen}
          title=""
          message={
            translatorService.Tranlate(
              'INTEROGATE_ALTERNATIVE_PARTS_DIALOG_TITLE',
              'Disponibilitatea pieselor va fi afisata conform adresei'
            ) +
            ' ' +
            (state.selectedOrganizationBranch?.organizationName ?? '') +
            ' ' +
            (state.selectedOrganizationBranch?.organizationAddress ?? '')
          }
          confirmLabel={translatorService.Tranlate('WORKFLOW_DOCUMENTS_TYPE_TAB_YES', 'Da')}
          cancelLabel={translatorService.Tranlate('WORKFLOW_DOCUMENTS_TYPE_TAB_NO', 'Nu')}
          onConfirm={() => {
            setIsOrgPartnerConfirmOpen(false);
            if (gatedAction) {
              gatedAction();
            }
          }}
          onClose={() => setIsOrgPartnerConfirmOpen(false)}
        />
      </Dialog>
  );
};

export default AlternativeCalculationDialog;
